/**
 * Phone handling.
 *
 * 97% of the 7540 exported customers have a phone number and only 71% have an email, so
 * the phone number is the de-facto primary identifier for this business. It has to be a
 * first-class search key, which means every stored number needs one canonical form the
 * index can match regardless of how anyone typed it.
 *
 * All three tenants are in Quebec (area codes 514, 438, 450, 579, 819, 873, 418, 581,
 * 367, 263), so bare 10-digit input is assumed to be Canadian.
 */

/** Digits only, last 10 significant. `(514) 966-2020`, `1-514-966-2020` → `5149662020`. */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null;
  const digits = input.replace(/\D/g, '');
  if (digits.length < 10) return digits.length ? digits : null;
  // Strip a leading North American country code, keep the last 10.
  return digits.slice(-10);
}

/** True when the input looks like the user is typing a phone number rather than a name. */
export function looksLikePhone(input: string): boolean {
  const digits = input.replace(/\D/g, '');
  if (digits.length < 3) return false;
  // Mostly digits, allowing the usual separators.
  return digits.length / input.replace(/\s/g, '').length > 0.6;
}

/**
 * Display form. French Canadian convention writes `514 966-2020`; English Canadian
 * commonly `(514) 966-2020`. Extensions are kept as typed.
 */
export function formatPhone(input: string | null | undefined, locale: 'fr' | 'en' = 'fr'): string {
  const n = normalizePhone(input);
  if (!n || n.length !== 10) return input ?? '';
  const [area, exchange, line] = [n.slice(0, 3), n.slice(3, 6), n.slice(6)];
  return locale === 'fr' ? `${area} ${exchange}-${line}` : `(${area}) ${exchange}-${line}`;
}

/** E.164 for a telephony provider. */
export function toE164(input: string | null | undefined, defaultCountry = '1'): string | null {
  const n = normalizePhone(input);
  if (!n) return null;
  return n.length === 10 ? `+${defaultCountry}${n}` : `+${n}`;
}

/**
 * Search terms for one phone number: the canonical 10 digits plus the trailing 7, so a
 * user who only remembers the local part still finds the customer.
 */
export function phoneSearchTerms(input: string | null | undefined): string[] {
  const n = normalizePhone(input);
  if (!n) return [];
  return n.length === 10 ? [n, n.slice(3)] : [n];
}
